<template>
  <div class="scheduled-sidebar">
    <layout-menu :items="scheduledSidebarRoutes" />
  </div>
</template>

<script lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import { computed, defineComponent } from '@/composition/vue/compositionApi'
import {
  getMainSidebarRoutesResolverService,
  getRouterService,
  getScheduledEmailService,
  getScheduledService,
  getTranslateService,
} from '@/core/container/ioc'
import type { PageContentSidebarItem } from '@/components/layout/pageContent/sidebar/types'
import LayoutMenu from '@/components/layout/LayoutMenu.vue'
import type { ScheduledFacetType } from '@/services/domain/scheduled/types'
import type { IconName } from '@/assets/icons/icons'
import { ScheduledType } from '@/components/views/scheduled/types'
import { useLayoutMenuScheduledFaceter } from '@/composition/domain/scheduled'
import type { TmRoutes, TmRoutesDictionary } from '@/services/route/routerTypes'
import { ScheduledRoutes } from '@/routes/user/scheduled/types'
import { ScheduledEmailStatus } from '@/data/repositories/domain/scheduledEmail/types'
import ScheduledMessage from '@/data/models/domain/scheduled/ScheduledMessage'
import ScheduledEmail from '@/data/models/domain/ScheduledEmail'
import TmAbsurdError from '@/core/error/tmAbsurdError'
import { useRoutePrefetch } from '@/composition/useRoutePrefetch'

export default defineComponent({
  components: {
    LayoutMenu,
  },
  setup() {
    const translateService = getTranslateService()
    const mainSidebarRoutesResolverService = getMainSidebarRoutesResolverService()
    const routerService = getRouterService()
    const scheduledService = getScheduledService()
    const scheduledEmailService = getScheduledEmailService()

    const { getNumberGroupByType } = useLayoutMenuScheduledFaceter()
    const getNumber = (type: ScheduledFacetType) => {
      const numberGroup = getNumberGroupByType(type)
      if (numberGroup.sms === null && numberGroup.email === null) {
        return null
      }
      let total = numberGroup.sms ?? 0
      if (numberGroup.email) {
        total += numberGroup.email
      }
      return total
    }

    const currentRoute = computed(() => routerService.getCurrentRoute() as TmRoutes)

    type RoutesKeys = ReturnType<typeof mainSidebarRoutesResolverService.getScheduledSectionRoutes>[number]['name']
    type RoutesInfo = Record<
      RoutesKeys,
      {
        title: string
        number?: number | null
        icon: IconName
        overrideActive?: boolean
      }
    >
    const emailRoutes: Record<Exclude<RoutesKeys, 'calendar'>, { name: string }> = {
      upcoming: {
        name: ScheduledRoutes.upcomingOverviewEmail,
      },
      paused: {
        name: ScheduledRoutes.pausedOverviewEmail,
      },
      notSent: {
        name: ScheduledRoutes.notSentOverviewEmail,
      },
      completed: {
        name: ScheduledRoutes.completedOverviewEmail,
      },
    }
    const message = computed(() => {
      const route = currentRoute.value
      if (route.name === ScheduledRoutes.detailsEmail) {
        return scheduledEmailService.findEntityByIdOrNull(route.params.messageId)
      }
      if (route.name.startsWith(ScheduledRoutes.details) || route.name.startsWith(ScheduledRoutes.edit)) {
        const typedRoute = route as TmRoutesDictionary[ScheduledRoutes.details | ScheduledRoutes.edit]
        return scheduledService.findEntityByIdOrNull(typedRoute.params.messageId)
      }
      return null
    })

    const isOverrideActive = (scheduledType: ScheduledType, scheduledEmailStatus?: ScheduledEmailStatus) => {
      if (message.value === null) {
        return false
      }
      if (message.value instanceof ScheduledMessage) {
        return message.value.status === scheduledType
      }
      if (message.value instanceof ScheduledEmail) {
        return message.value.status === scheduledEmailStatus
      }
      throw new TmAbsurdError(message.value)
    }

    const routesInfo = computed<RoutesInfo>(() => {
      return {
        upcoming: {
          title: translateService.t('scheduledSideBar.upcoming'),
          number: getNumber('upcoming'),
          icon: 'tmi-event-calendar-upcoming',
          overrideActive: isOverrideActive(ScheduledType.UPCOMING, ScheduledEmailStatus.New),
        },
        paused: {
          title: translateService.t('scheduledSideBar.paused'),
          number: getNumber('paused'),
          icon: 'pause',
          overrideActive: isOverrideActive(ScheduledType.PAUSED, ScheduledEmailStatus.Paused),
        },
        notSent: {
          title: translateService.t('scheduledSideBar.notSent'),
          number: getNumber('notSent'),
          icon: 'warning',
          overrideActive: isOverrideActive(ScheduledType.NOT_SENT),
        },
        completed: {
          title: translateService.t('scheduledSideBar.completed'),
          number: getNumber('completed'),
          icon: 'tmi-check-circle',
          overrideActive: isOverrideActive(ScheduledType.COMPLETED, ScheduledEmailStatus.Completed),
        },
        calendar: {
          title: translateService.t('scheduledSideBar.calendar'),
          icon: 'tmi-event',
        },
      }
    })

    const scheduledSidebarRoutes = computed<PageContentSidebarItem[]>(() => {
      let getRoute: (routeKey: RoutesKeys, route: RouteLocationRaw) => RouteLocationRaw = (_routeKey, route) => route
      if (Object.values(emailRoutes).find((t) => t.name === currentRoute.value.name)) {
        getRoute = (routeKey, route) => {
          if (routeKey === 'calendar') {
            return route
          }
          const typedRouteKey: keyof typeof emailRoutes = routeKey
          return emailRoutes[typedRouteKey]
        }
      }
      return mainSidebarRoutesResolverService.getScheduledSectionRoutes().map((t) => {
        const routeInfo = routesInfo.value[t.name]
        return {
          itemType: 'link',
          title: routeInfo.title,
          route: getRoute(t.name, t.route),
          number: routeInfo.number,
          icon: routeInfo.icon,
          overrideActive: routeInfo.overrideActive ?? false,
        }
      })
    })

    const routesToPrefetch = computed<string[]>(() => {
      return [
        ...mainSidebarRoutesResolverService.getScheduledSectionRoutes().map((t) => t.route.name),
        ...Object.values(emailRoutes).map((t) => t.name),
      ]
    })
    useRoutePrefetch(routesToPrefetch)

    return {
      scheduledSidebarRoutes,
    }
  },
})
</script>
